/**
 * Session Service
 * Handles ADK session creation for conversation continuity
 */

import { getApiConfig } from '../../../api/config';
import { getFirebaseAuth } from '../../../config/firebase';
import { authTokenCache } from '../../../utils/authTokenCache';

/**
 * Get authentication token from Firebase Auth with caching
 * 
 * Uses authTokenCache to avoid repeated Firebase API calls and ensure
 * tokens are always fresh (Firebase tokens expire after ~1 hour).
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const auth = getFirebaseAuth();
    
    if (!auth) {
      return null;
    }
    
    if (!auth.currentUser) {
      return null;
    }
    
    // Use cached token instead of fetching new one every time
    const token = await authTokenCache.getToken(auth);
    
    return token;
  } catch (error) {
    return null;
  }
}

export interface CreateSessionResponse {
  session_id: string;  // ADK session_id (simple string ID, e.g., "123456789" or "custom-session-123")
  created_at: string;
}

/**
 * Create a new ADK session for the user
 * 
 * @returns Promise resolving to CreateSessionResponse with ADK session_id
 * @throws Error if session creation fails
 */
export async function createSession(): Promise<CreateSessionResponse> {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const config = getApiConfig();
  const endpoint = `${config.baseURL}/api/v1/ai-assist/sessions`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to create session: HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  return data as CreateSessionResponse;
}

/**
 * Extract short session ID from ADK session ID
 * 
 * Note: ADK session IDs are already simple strings (numeric/alphanumeric).
 * This function is kept for backward compatibility and handles both:
 * - Simple IDs: "123456789" -> "123456789"
 * - Full resource names (if any): "projects/.../sessions/123456789" -> "123456789"
 * 
 * @param sessionId ADK session ID (simple string or full resource name)
 * @returns Short session ID (extracted if full resource name, otherwise as-is)
 */
export function getShortSessionId(sessionId: string | null | undefined): string {
  // Handle null/undefined
  if (!sessionId) {
    return '';
  }
  // If it's a full resource name, extract the ID part
  if (sessionId.includes('/sessions/')) {
    return sessionId.split('/sessions/').pop() || sessionId;
  }
  // Otherwise, it's already a simple ID
  return sessionId;
}

/**
 * Storage key for session ID in localStorage
 */
const SESSION_STORAGE_KEY = 'flowrithm_ai_assist_session_id';

/**
 * Get stored session ID from localStorage
 * @returns Stored session ID or null if not found
 */
export function getStoredSessionId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(SESSION_STORAGE_KEY) || sessionStorage.getItem(SESSION_STORAGE_KEY);
}

/**
 * Store session ID in localStorage (persists across tabs) or sessionStorage (per tab)
 * @param sessionId Session ID to store
 * @param persistent If true, stores in localStorage; otherwise uses sessionStorage
 */
export function storeSessionId(sessionId: string, persistent: boolean = true): void {
  if (typeof window === 'undefined') {
    return;
  }
  
  if (persistent) {
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    // Also store in sessionStorage for backward compatibility
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } else {
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  }
}

/**
 * Clear stored session ID
 */
export function clearStoredSessionId(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(SESSION_STORAGE_KEY);
  sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

/**
 * Create a new session and store it
 * This is a convenience function that creates a session and stores it automatically
 * @param persistent If true, stores in localStorage; otherwise uses sessionStorage
 * @returns Promise resolving to CreateSessionResponse with ADK session_id
 */
export async function createAndStoreSession(persistent: boolean = true): Promise<CreateSessionResponse> {
  const response = await createSession();
  storeSessionId(response.session_id, persistent);
  return response;
}

export interface SessionInfo {
  session_id: string;  // ADK session ID
  display_name: string | null;  // Display name if set
}

export interface ListSessionsResponse {
  sessions: SessionInfo[];  // List of session info with display names
  count: number;       // Total number of sessions
}

/**
 * List all ADK sessions for the authenticated user
 * 
 * @returns Promise resolving to ListSessionsResponse with list of session IDs
 * @throws Error if session listing fails
 */
export async function listSessions(): Promise<ListSessionsResponse> {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const config = getApiConfig();
  const endpoint = `${config.baseURL}/api/v1/ai-assist/sessions`;

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to list sessions: HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  return data as ListSessionsResponse;
}

export interface GetSessionMessagesResponse {
  session_id: string;
  messages: MessageEvent[];
  count: number;
}

export interface MessageEvent {
  id: string;
  author: string;
  content: string;
  timestamp?: string | null;
}

/**
 * Get message history for a specific ADK session
 * 
 * @param sessionId ADK session ID to retrieve messages for
 * @returns Promise resolving to GetSessionMessagesResponse with list of message events
 * @throws Error if message retrieval fails
 */
export async function getSessionMessages(sessionId: string): Promise<GetSessionMessagesResponse> {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const config = getApiConfig();
  const endpoint = `${config.baseURL}/api/v1/ai-assist/sessions/${sessionId}/messages`;

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to get session messages: HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  return data as GetSessionMessagesResponse;
}

/**
 * Update the display name for a session
 * 
 * @param sessionId ADK session ID
 * @param displayName New display name for the session
 * @returns Promise resolving to success message
 * @throws Error if update fails
 */
export async function updateSessionDisplayName(sessionId: string, displayName: string): Promise<void> {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const config = getApiConfig();
  const endpoint = `${config.baseURL}/api/v1/ai-assist/sessions/${sessionId}`;

  const response = await fetch(endpoint, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ display_name: displayName }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update session display name: HTTP ${response.status}: ${errorText}`);
  }
}

/**
 * Delete a session
 * 
 * @param sessionId ADK session ID to delete
 * @returns Promise resolving to success message
 * @throws Error if deletion fails
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const token = await getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  const config = getApiConfig();
  const endpoint = `${config.baseURL}/api/v1/ai-assist/sessions/${sessionId}`;

  const response = await fetch(endpoint, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete session: HTTP ${response.status}: ${errorText}`);
  }
}

